import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

function createData(id, taskId, Title, dayno, taskno, Completed, Created, type, taskType) {
  return taskType ? {
    id, taskId, Title, dayno, taskno, Completed, Created, type: taskType,
  } : {
    id, taskId, Title, dayno, taskno, Completed, Created, type,
  };
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => { return descendingComparator(a, b, orderBy); }
    : (a, b) => { return -descendingComparator(a, b, orderBy); };
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => { return [el, index]; });
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => { return el[0]; });
}

const headCells = [
  {
    id: 'name', start: true, disablePadding: true, label: 'Task ID',
  },
  { id: 'Title', disablePadding: false, label: 'Task Title' },
  {
    id: 'dayno', disablePadding: false, sortable: true, label: 'Day',
  },
  {
    id: 'taskno', disablePadding: false, sortable: true, label: 'Task',
  },
  {
    id: 'Completion', disablePadding: false, sortable: true, label: 'Completion Date',
  },
  {
    id: 'Creation', disablePadding: false, sortable: true, label: 'Creation Date',
  },
  { id: 'Type', disablePadding: false, label: 'Task Type' },
];

function EnhancedTableHead(props) {
  const {
    classes, order, orderBy, onRequestSort,
  } = props;
  const createSortHandler = (property) => {
    return (event) => {
      onRequestSort(event, property);
    };
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        {headCells.map((headCell) => {
          return (
            <TableCell
              key={headCell.id}
              align={headCell.start ? 'left' : 'right'}
              padding={headCell.disablePadding ? 'none' : 'default'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.sortable
                ? (
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                )
                : <span>{headCell.label}</span>}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const useStyles = makeStyles((theme) => {
  return {
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  };
});

export default function EnhancedTable(props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('Completion');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const rows = [];
  const { completedTasks } = props;
  completedTasks.forEach((task) => {
    return rows.push(createData(
      task._id,
      task.task._id,
      task.task.title,
      task.task.day,
      task.task.task,
      task.completedDate,
      task.createdAt,
      task.task.type,
      task.task.taskType,
    ));
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Typography style={{ flex: '1 1 100%', padding: 10 }} variant="h6" id="tableTitle" component="div">
          Completed Tasks
        </Typography>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="small"
            aria-label="completed tasks table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.id}
                    >
                      <TableCell padding="checkbox">
                        <ArrowForwardIosIcon style={{ fontSize: 10 }} />
                      </TableCell>
                      <TableCell component="th" id={row.id} scope="row" padding="none">
                        {row.taskId}
                      </TableCell>
                      <TableCell align="right">{row.Title}</TableCell>
                      <TableCell align="right">{row.dayno}</TableCell>
                      <TableCell align="right">{row.taskno}</TableCell>
                      <TableCell align="right">{row.Completed}</TableCell>
                      <TableCell align="right">{row.Created}</TableCell>
                      <TableCell align="right">{row.type}</TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
